import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { BookmarkCheck, Eye, Pencil, XCircle, ShoppingCart, FileText, Mail, Phone, MapPin, Truck } from 'lucide-react';
import ShippingCountries from '../components/ShippingCountries';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v || 0);
const when = (iso) => iso ? new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
const STATUS = {
  reservada: { label: 'Reservada', cls: 'bg-sky-100 text-sky-700 border-sky-300' },
  vendida: { label: 'Vendida', cls: 'bg-green-100 text-green-700 border-green-300' },
  cancelada: { label: 'Cancelada', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
};
const FIELDS = [['name', 'Nombre'], ['surname', 'Apellidos'], ['document_id', 'DNI / Pasaporte'], ['phone', 'Teléfono'], ['email', 'Email'], ['country', 'País'], ['address', 'Dirección']];

async function openPdf(res) {
  try {
    const r = await axios.get(`${API}/shop/reservations/${res.id}/proforma.pdf`, { responseType: 'blob' });
    window.open(URL.createObjectURL(r.data), '_blank');
  } catch { toast.error('No se pudo abrir la proforma'); }
}

function ViewDialog({ res, onClose }) {
  if (!res) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="reservation-view-dialog">
        <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">Reserva · Ref. {res.bicycle_ref}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="font-semibold text-slate-800">{res.bicycle_model} · Talla {res.bicycle_size}{res.bicycle_year ? ` · ${res.bicycle_year}` : ''}</p>
            <div className="text-sm mt-1 space-y-0.5">
              <div className="flex justify-between"><span className="text-slate-500">Bicicleta</span><span>{eur(res.price)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Envío{res.country ? ` · ${res.country}` : ''}</span><span>{res.shipping_cost > 0 ? eur(res.shipping_cost) : 'Gratis'}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-1"><span className="font-semibold">Total (IVA incl.)</span><span className="text-orange-600 font-bold text-lg" data-testid="view-res-total">{eur(res.total ?? res.price)}</span></div>
            </div>
            <p className="text-xs text-slate-500">Reservada el {when(res.created_at)} · Proforma {res.proforma_number || '—'}</p>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {FIELDS.map(([k, l]) => <div key={k} className={k === 'address' ? 'col-span-2' : ''}><p className="text-[11px] uppercase text-slate-400">{l}</p><p className="text-slate-800">{res[k] || '—'}</p></div>)}
            {res.message && <div className="col-span-2"><p className="text-[11px] uppercase text-slate-400">Mensaje del cliente</p><p className="text-slate-700 italic">{res.message}</p></div>}
            {res.admin_notes && <div className="col-span-2"><p className="text-[11px] uppercase text-slate-400">Notas internas</p><p className="text-slate-700">{res.admin_notes}</p></div>}
          </div>
          {res.email_status && <p className="text-xs text-slate-500">Email cliente: {res.email_status.client ? 'enviado' : 'no enviado'} · Copia admin ({res.email_status.admin_to || 'sin configurar'}): {res.email_status.admin ? 'enviada' : 'no enviada'}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => openPdf(res)} className="gap-1" data-testid="reservation-pdf-btn"><FileText size={14} /> Ver proforma PDF</Button>
          <Link to={`/bicycles/${res.bicycle_id}`}><Button variant="outline">Ir a la bicicleta</Button></Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ res, onClose, onSaved }) {
  const [form, setForm] = useState(res ? { ...res } : {});
  const [saving, setSaving] = useState(false);
  if (!res) return null;
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = Object.fromEntries([...FIELDS.map(([k]) => k), 'admin_notes'].map((k) => [k, form[k] ?? '']));
      payload.price = parseFloat(form.price) || res.price;
      await axios.put(`${API}/shop/reservations/${res.id}`, payload);
      toast.success('Reserva actualizada (proforma regenerada)'); onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="reservation-edit-dialog">
        <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">Modificar reserva · Ref. {res.bicycle_ref}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, l]) => (
            <div key={k} className={`space-y-1 ${k === 'address' ? 'col-span-2' : ''}`}>
              <Label className="text-xs">{l}</Label>
              <Input value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-res-${k}`} />
            </div>
          ))}
          <div className="space-y-1"><Label className="text-xs">Precio bicicleta (IVA incl.) · el envío se recalcula según el país</Label><Input type="number" step="0.01" value={form.price ?? ''} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="edit-res-price" /></div>
          <div className="space-y-1"><Label className="text-xs">Notas internas</Label><Input value={form.admin_notes || ''} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} data-testid="edit-res-notes" /></div>
          <DialogFooter className="col-span-2"><Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="edit-res-save">Guardar cambios</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SaleDialog({ res, onClose, onDone }) {
  const [paid, setPaid] = useState('');
  const [saving, setSaving] = useState(false);
  if (!res) return null;
  const pend = Math.max(0, (res.price || 0) - (parseFloat(paid) || 0));
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axios.post(`${API}/shop/reservations/${res.id}/convert-sale`, { amount_paid: parseFloat(paid) || 0 });
      toast.success(pend > 0 ? 'Vendida · pendiente de cobro' : 'Reserva convertida en venta'); onDone(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al convertir en venta'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reservation-sale-dialog">
        <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">Convertir en venta · Ref. {res.bicycle_ref}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-slate-600">Cliente <b>{res.name} {res.surname}</b> · Precio bicicleta <b className="text-orange-600">{eur(res.price)}</b>{res.shipping_cost > 0 ? <span className="text-slate-500"> (+ envío {eur(res.shipping_cost)} = total {eur(res.total)})</span> : null}</p>
          <div className="space-y-1">
            <Label>Importe ya cobrado (transferencia recibida)</Label>
            <div className="flex gap-2">
              <Input type="number" step="0.01" min="0" max={res.price} value={paid} onChange={(e) => setPaid(e.target.value)} placeholder="0.00" data-testid="sale-paid-input" />
              <Button type="button" variant="outline" onClick={() => setPaid(String(res.price))} data-testid="sale-paid-full">Cobro completo</Button>
            </div>
            <p className="text-xs text-slate-500">{pend > 0 ? `Quedará pendiente de cobro ${eur(pend)} (aparecerá en "Vendidas pendiente de cobro")` : 'Venta cobrada íntegramente'}</p>
          </div>
          <DialogFooter><Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="sale-confirm-btn">Confirmar venta</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ShopReservations() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('reservada');
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(null);
  const [sale, setSale] = useState(null);
  const [shipping, setShipping] = useState(false);

  const load = async () => {
    try { const r = await axios.get(`${API}/shop/reservations`); setItems(r.data); }
    catch { toast.error('Error al cargar las reservas'); }
  };
  useEffect(() => { load(); }, []);

  const list = useMemo(() => items.filter((r) => filter === 'todas' || r.status === filter), [items, filter]);
  const counts = useMemo(() => items.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {}), [items]);

  const cancel = async (r) => {
    if (!window.confirm(`¿Cancelar la reserva de ${r.name} ${r.surname}? La bicicleta ${r.bicycle_ref} volverá a estar disponible en la Tienda Online.`)) return;
    try { await axios.post(`${API}/shop/reservations/${r.id}/cancel`); toast.success('Reserva cancelada · bicicleta disponible de nuevo'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error al cancelar'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reservations-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Barlow_Condensed'] text-slate-800 flex items-center gap-2"><BookmarkCheck className="text-sky-500" /> Reservadas</h1>
          <p className="text-slate-500 text-sm">Reservas recibidas desde la Tienda Online · Disponible → Reservada → Vendida</p>
        </div>
        <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" className="gap-1" onClick={() => setShipping(true)} data-testid="shipping-config-btn"><Truck size={14} /> Países de envío</Button>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {[['reservada', 'Activas'], ['vendida', 'Vendidas'], ['cancelada', 'Canceladas'], ['todas', 'Todas']].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 text-sm rounded-md ${filter === k ? 'bg-white shadow font-semibold text-slate-900' : 'text-slate-500'}`} data-testid={`res-filter-${k}`}>
              {l}{k !== 'todas' && counts[k] ? ` (${counts[k]})` : ''}
            </button>
          ))}
        </div>
        </div>
      </div>

      <Card className="border-slate-200"><CardContent className="p-0">
        {list.length === 0 ? <div className="p-12 text-center text-slate-500" data-testid="reservations-empty">No hay reservas en este estado</div> : (
          <Table data-testid="reservations-table">
            <TableHeader><TableRow className="bg-slate-50">
              {['Bicicleta', 'Total', 'Cliente', 'Contacto', 'Fecha y hora', 'Estado', ''].map((h) => <TableHead key={h} className="text-xs font-bold uppercase text-slate-500">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {list.map((r) => (
                <TableRow key={r.id} data-testid={`reservation-row-${r.id}`}>
                  <TableCell>
                    <Link to={`/bicycles/${r.bicycle_id}`} className="font-semibold text-slate-800 hover:text-orange-600">Ref. {r.bicycle_ref}</Link>
                    <div className="text-xs text-slate-500">{r.bicycle_model} · Talla {r.bicycle_size}</div>
                    {r.proforma_number && <div className="text-[11px] text-slate-400">Proforma {r.proforma_number}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="font-semibold text-orange-600" data-testid={`res-total-${r.id}`}>{eur(r.total ?? r.price)}</div>
                    <div className="text-[11px] text-slate-500">Bici {eur(r.price)} + envío {r.shipping_cost > 0 ? eur(r.shipping_cost) : '0 €'}</div>
                  </TableCell>
                  <TableCell><div className="font-medium">{r.name} {r.surname}</div><div className="text-xs text-slate-500">{r.document_id}</div></TableCell>
                  <TableCell className="text-xs text-slate-600 space-y-0.5">
                    <div className="flex items-center gap-1"><Mail size={11} /> {r.email}</div>
                    <div className="flex items-center gap-1"><Phone size={11} /> {r.phone}</div>
                    <div className="flex items-center gap-1 text-slate-400"><MapPin size={11} /> {r.country}</div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">{when(r.created_at)}</TableCell>
                  <TableCell><Badge className={STATUS[r.status]?.cls}>{STATUS[r.status]?.label || r.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Ver" onClick={() => setView(r)} data-testid={`res-view-${r.id}`}><Eye size={15} /></Button>
                      {r.status === 'reservada' && <>
                        <Button size="icon" variant="ghost" className="h-8 w-8" title="Modificar" onClick={() => setEdit(r)} data-testid={`res-edit-${r.id}`}><Pencil size={15} /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" title="Cancelar / liberar" onClick={() => cancel(r)} data-testid={`res-cancel-${r.id}`}><XCircle size={15} /></Button>
                        <Button size="sm" className="h-8 bg-green-600 hover:bg-green-700 text-white gap-1" onClick={() => setSale(r)} data-testid={`res-sale-${r.id}`}><ShoppingCart size={14} /> Vender</Button>
                      </>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <ViewDialog res={view} onClose={() => setView(null)} />
      <EditDialog key={edit?.id} res={edit} onClose={() => setEdit(null)} onSaved={load} />
      <SaleDialog res={sale} onClose={() => setSale(null)} onDone={load} />
      <ShippingCountries open={shipping} onClose={() => setShipping(false)} />
    </div>
  );
}
